import { ModelDisseminationStatus } from '@app/api/dissemination/types';
import { ModelTimeline } from './ModelTimeline';
import { FORECAST_MODELS } from '@app/common/static-data/models';

// Helper function to get model display name
function getModelDisplayName(modelId: string): string {
  const model = FORECAST_MODELS.find((m) => m.id === modelId);
  return model?.label || modelId.toUpperCase();
}

interface SharedTimelineProps {
  statusPerModel: Record<string, ModelDisseminationStatus>;
  modelOrder: string[];
}

export function SharedTimeline({ statusPerModel, modelOrder }: SharedTimelineProps) {
  // Calculate the time domain across all models
  const calculateTimeDomain = () => {
    let minTime = Infinity;
    let maxTime = -Infinity;

    Object.values(statusPerModel).forEach((modelStatus) => {
      modelStatus.status.forEach((forecast) => {
        const time = new Date(forecast.init_time).getTime();
        minTime = Math.min(minTime, time);
        maxTime = Math.max(maxTime, time);
      });
    });

    // Add padding (5% on each side)
    const range = maxTime - minTime;
    const padding = range * 0.05;

    return {
      start: new Date(minTime - padding),
      end: new Date(maxTime + padding),
    };
  };

  const timeDomain = calculateTimeDomain();

  // Generate time axis labels
  const generateTimeLabels = () => {
    const start = timeDomain.start.getTime();
    const end = timeDomain.end.getTime();
    const range = end - start;
    const labelCount = 5;
    const labels: Array<{ position: number; label: string }> = [];

    for (let i = 0; i <= labelCount; i++) {
      const time = start + (range * i) / labelCount;
      const date = new Date(time);
      const position = (i / labelCount) * 100;

      labels.push({
        position,
        label: date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'UTC',
        }),
      });
    }

    return labels;
  };

  const timeLabels = generateTimeLabels();

  if (Object.keys(statusPerModel).length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        No dissemination status data available
      </div>
    );
  }

  // Sort models according to the order defined in modelOrder
  const sortedModelEntries = modelOrder
    .filter((modelId) => statusPerModel[modelId]) // Only include models that have data
    .map((modelId) => [modelId, statusPerModel[modelId]] as [string, ModelDisseminationStatus]);

  return (
    <div className="space-y-0">
      <div className="bg-white dark:bg-theme-dark-bg rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <h2 className="text-lg font-bold mb-4 dark:text-theme-dark-text-primary">Timeline View</h2>

        <div className="space-y-0">
          {sortedModelEntries.map(([modelId, modelStatus]) => (
            <ModelTimeline
              key={modelId}
              modelName={getModelDisplayName(modelId)}
              status={modelStatus.status}
              timeDomain={timeDomain}
            />
          ))}
        </div>

        {/* Time axis */}
        <div className="flex items-center mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="w-40 flex-shrink-0" />
          <div className="flex-1 relative h-8">
            {timeLabels.map((label, index) => {
              // Adjust alignment for edge labels to prevent cutoff
              let transform = 'translateX(-50%)';
              if (index === 0) {
                transform = 'translateX(0)'; // Left-align first label
              } else if (index === timeLabels.length - 1) {
                transform = 'translateX(-100%)'; // Right-align last label
              }

              return (
                <div
                  key={index}
                  className="absolute text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap"
                  style={{
                    left: `${label.position}%`,
                    transform,
                  }}
                >
                  {label.label}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="bg-white dark:bg-theme-dark-bg rounded-lg border border-gray-200 dark:border-gray-700 p-4 mt-4">
        <h3 className="text-sm font-semibold mb-3 dark:text-theme-dark-text-primary">
          Status Legend
        </h3>
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-500 rounded" />
            <span className="text-sm text-gray-700 dark:text-gray-300">On Time (early)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-yellow-500 rounded" />
            <span className="text-sm text-gray-700 dark:text-gray-300">Delayed (&lt;30min)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-red-500 rounded" />
            <span className="text-sm text-gray-700 dark:text-gray-300">Delayed (≥30min)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-gray-400 dark:bg-gray-600 rounded" />
            <span className="text-sm text-gray-700 dark:text-gray-300">Missing</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-500 rounded animate-pulse" />
            <span className="text-sm text-gray-700 dark:text-gray-300">In Progress (flashing)</span>
          </div>
        </div>
      </div>
    </div>
  );
}
